// The linked browser's keys (docs/09 "Web UI", docs/07 "Linked devices"): an ECDH P-256
// pair that receives conversation keys and an ECDSA P-256 pair that signs requests, both
// generated non-extractable by WebCrypto so no script, extension or export path can read
// the private halves. The crate's signature wire format (D27) is what WebCrypto produces
// natively: 65-byte uncompressed SEC1 public keys and 64-byte `r ‖ s` signatures over
// SHA-256.
import { concat, owned, utf8 } from "../proto/bytes";
import type { SigAlg } from "../proto/frames";

const ECDH = { name: "ECDH", namedCurve: "P-256" } as const;
const ECDSA = { name: "ECDSA", namedCurve: "P-256" } as const;
const ECDSA_SHA256 = { name: "ECDSA", hash: "SHA-256" } as const;

export const AUTH_ALG: SigAlg = "ecdsa_p256_sha256";

/** The protocol steps a device signature is bound to (ocs_proto::sig::DOMAIN_*). */
export type Domain = "ocs/v0/request" | "ocs/v0/inbound" | "ocs/v0/approval";

export interface DeviceKeys {
  encryption: CryptoKeyPair;
  auth: CryptoKeyPair;
  /** Uncompressed SEC1 point of the ECDH key, as enrolled. */
  encryptionPublicKey: Uint8Array;
  /** Uncompressed SEC1 point of the ECDSA key, as enrolled. */
  authPublicKey: Uint8Array;
}

export async function generateDeviceKeys(): Promise<DeviceKeys> {
  const encryption = await crypto.subtle.generateKey(ECDH, false, ["deriveBits"]);
  const auth = await crypto.subtle.generateKey(ECDSA, false, ["sign"]);
  return {
    encryption,
    auth,
    encryptionPublicKey: new Uint8Array(await crypto.subtle.exportKey("raw", encryption.publicKey)),
    authPublicKey: new Uint8Array(await crypto.subtle.exportKey("raw", auth.publicKey)),
  };
}

/** `domain ‖ 0x00 ‖ message`, the bytes every ocs-proto signature covers. */
export function signingInput(domain: Domain, message: Uint8Array): Uint8Array {
  return concat(utf8.encode(domain), new Uint8Array([0]), message);
}

export async function signDomain(key: CryptoKey, domain: Domain, message: Uint8Array): Promise<Uint8Array> {
  return new Uint8Array(await crypto.subtle.sign(ECDSA_SHA256, key, owned(signingInput(domain, message))));
}

export async function verifyDomain(
  publicKey: Uint8Array,
  domain: Domain,
  message: Uint8Array,
  signature: Uint8Array,
): Promise<boolean> {
  const key = await crypto.subtle.importKey("raw", owned(publicKey), ECDSA, false, ["verify"]);
  return crypto.subtle.verify(ECDSA_SHA256, key, owned(signature), owned(signingInput(domain, message)));
}

/**
 * The raw ECDH output (the x-coordinate) between the device's private key and `enc`, the
 * encapsulated key inside a wrapped CEK: the half of HPKE decapsulation the crate cannot do
 * with a non-extractable key (docs/09 "Shared crates").
 */
export async function ecdh(privateKey: CryptoKey, enc: Uint8Array): Promise<Uint8Array> {
  const peer = await crypto.subtle.importKey("raw", owned(enc), ECDH, false, []);
  return new Uint8Array(await crypto.subtle.deriveBits({ name: "ECDH", public: peer }, privateKey, 256));
}
